import { useEffect, useMemo, useState } from "react";
import { Play, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Slider } from "../ui/slider";
import { Button } from "../ui/button";
import { Label } from "../ui/label";

export const VoicePicker = ({ cfg, set }) => {
  const [voices, setVoices] = useState([]);
  const [lang, setLang] = useState("es_ES");
  const [busy, setBusy] = useState(false);
  const [styles, setStyles] = useState([]);

  useEffect(() => { api.get("/voices").then((r) => setVoices(r.data)); api.get("/voices/styles").then((r) => setStyles(r.data)); }, []);
  useEffect(() => { const v = voices.find((x) => x.key === cfg.voice); if (v) setLang(v.lang_code); }, [voices, cfg.voice]); // eslint-disable-line

  const langs = useMemo(() => {
    const m = new Map();
    voices.forEach((v) => m.set(v.lang_code, `${v.lang_family === "es" ? "★ " : ""}${v.language_native} · ${v.country} (${voices.filter((x) => x.lang_code === v.lang_code).length})`));
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [voices]);
  const list = voices.filter((v) => v.lang_code === lang);
  const current = voices.find((v) => v.key === cfg.voice);

  const pickLang = (l) => { setLang(l); const first = voices.find((v) => v.lang_code === l); if (first) set({ voice: first.key, speaker: null }); };

  const preview = async () => {
    setBusy(true);
    if (current && !current.installed) toast.info(`Descargando modelo ${current.name} (${current.size_mb} MB)…`);
    const audio = new Audio(`${API}/voices/preview?key=${cfg.voice}${cfg.speaker ? `&speaker=${encodeURIComponent(cfg.speaker)}` : ""}&style=${cfg.voice_style}`);
    audio.onplaying = () => setBusy(false);
    audio.onerror = () => { setBusy(false); toast.error("No se pudo generar la vista previa"); };
    audio.play().catch(() => setBusy(false));
  };

  return (
    <div className="space-y-4" data-testid="voice-picker">
      <div className="space-y-2">
        <Label className="text-xs text-zinc-400">Idioma ({langs.length})</Label>
        <Select value={lang} onValueChange={pickLang}>
          <SelectTrigger data-testid="voice-lang-select" className="bg-zinc-900/60"><SelectValue placeholder="Idioma" /></SelectTrigger>
          <SelectContent className="max-h-72">{langs.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="flex gap-2 items-end">
        <div className="space-y-2 flex-1">
          <Label className="text-xs text-zinc-400">Voz ({list.length})</Label>
          <Select value={cfg.voice} onValueChange={(v) => set({ voice: v, speaker: null })}>
            <SelectTrigger data-testid="voice-select" className="bg-zinc-900/60"><SelectValue placeholder="Voz" /></SelectTrigger>
            <SelectContent>{list.map((v) => <SelectItem key={v.key} value={v.key}>{v.name} · {v.quality}{v.installed ? " ✓" : ""}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button size="icon" variant="outline" data-testid="voice-preview-btn" onClick={preview} disabled={busy} className="border-white/10">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
        </Button>
      </div>
      <div className="space-y-2">
        <Label className="text-xs text-zinc-400">Estilo de locución ({styles.length})</Label>
        <div className="grid grid-cols-2 gap-1.5" data-testid="voice-style-grid">
          {styles.map((s) => (
            <button key={s.id} data-testid={`voice-style-${s.id}`} onClick={() => set({ voice_style: s.id })}
              className={`text-xs py-2 px-2.5 rounded-lg border text-left transition-colors ${cfg.voice_style === s.id ? "border-[#E8C47C] bg-[#E8C47C]/10 text-white" : "border-white/10 text-zinc-400 hover:border-white/25"}`}>{s.name}</button>
          ))}
        </div>
        <p className="text-[11px] text-zinc-500">{voices.filter((v) => v.lang_family === "es").length} voces en español (España, México, Argentina) × {styles.length} estilos = {voices.filter((v) => v.lang_family === "es").length * styles.length} combinaciones.</p>
      </div>
      {current?.num_speakers > 1 && (
        <div className="space-y-2">
          <Label className="text-xs text-zinc-400">Hablante ({current.speakers.length})</Label>
          <Select value={cfg.speaker || current.speakers[0]} onValueChange={(v) => set({ speaker: v })}>
            <SelectTrigger data-testid="voice-speaker-select" className="bg-zinc-900/60"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">{current.speakers.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      <div className="space-y-2">
        <Label className="text-xs text-zinc-400 flex justify-between">Velocidad <span className="font-mono-tc text-[#E8C47C]">{cfg.speed.toFixed(2)}x</span></Label>
        <Slider data-testid="voice-speed-slider" min={0.7} max={1.5} step={0.05} value={[cfg.speed]} onValueChange={([v]) => set({ speed: v })} />
      </div>
    </div>
  );
};
